import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

Font.register({
  family: "Lora",
  fonts: [
    { src: "/fonts/Lora/static/Lora-Regular.ttf" },
    { src: "/fonts/Lora/static/Lora-Medium.ttf", fontWeight: "medium" },
    { src: "/fonts/Lora/static/Lora-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Lora/static/Lora-Italic.ttf", fontStyle: "italic" },
    {
      src: "/fonts/Lora/static/Lora-BoldItalic.ttf",
      fontWeight: "bold",
      fontStyle: "italic",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "white",
    width: "100%",
    height: "100%",
    padding: "60px 80px",
    fontFamily: "Lora",
    fontWeight: "medium",
    overflow: "hidden",
  },
  border: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderStyle: "solid",
    borderWidth: 4,
    zIndex: -1,
    margin: "12px 20px",
  },
  cornerTopRight: {
    position: "absolute",
    top: "50%",
    right: 0,
    width: "50px",
    height: "45%",
  },
  cornerBottomLeft: {
    position: "absolute",
    top: "50%",
    left: 0,
    width: "50px",
    height: "45%",
  },
  title: {
    fontSize: 28,
    textAlign: "center",
    maxWidth: "500px",
    marginBottom: "16px",
    fontWeight: "bold",
  },
  section: {
    fontSize: 13,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "8px",
  },
  name: {
    fontSize: 26,
    textAlign: "center",
    fontWeight: "bold",
    fontStyle: "italic",
    marginVertical: "8px",
  },
  footer: {
    fontSize: 9,
    color: "grey",
    textAlign: "center",
    marginTop: "12px",
  },
  schoolLogo: {
    position: "absolute",
    top: "24px",
    left: "40px",
    width: "60px",
    height: "60px",
    aspectRatio: "1/1",
    objectFit: "contain",
  },
  platformLogo: {
    position: "absolute",
    top: "20px",
    right: "40px",
    width: "140px",
    height: "40px",
    objectFit: "contain",
  },
  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    padding: "0 80px",
    marginTop: "auto",
    marginBottom: "40px",
    fontSize: 12,
  },
  contentContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: "10px",
  },
  websiteSection: {
    position: "absolute",
    bottom: "16px",
    left: 0,
    right: 0,
    textAlign: "center",
    zIndex: 10,
  },
  websiteText: {
    fontSize: 10,
    color: "#667eea",
    fontWeight: "medium",
    letterSpacing: 1,
  },
  websiteDivider: {
    width: "80px",
    height: "1px",
    backgroundColor: "#667eea",
    margin: "4px auto",
    opacity: 0.6,
  },
  poweredByText: {
    fontSize: 8,
    color: "#999",
    marginTop: "2px",
    fontStyle: "italic",
  },
});

// Default text for each editable field of the certificate, used to prefill the
// on-sheet editor before the user overrides a specific field.
export function getDefaultCertificateTexts({
  type,
  duration,
  endDate,
  location,
  training,
  school,
  student: { fullName, birthDate, sex, birthPlace } = {},
}) {
  const isTraining = type === "training";
  const isVacation = type === "vacation";
  const trainingName = isTraining || isVacation ? training?.name : training?.titled;
  const schoolName = school?.name;
  const genderNe = sex?.toLowerCase() === "female" ? "Née" : "Né";

  const title = isVacation
    ? "ATTESTATION DE VACATIONS"
    : isTraining
      ? "ATTESTATION DE FORMATION"
      : "ATTESTATION DE STAGE";

  let mainText;
  if (isVacation) {
    const formateurTitle = sex?.toLowerCase() === "female" ? "formatrice" : "formateur";
    const genderStr = sex?.toLowerCase() === "female" ? "née" : "né";
    mainText = `${fullName}, ${genderStr} le ${birthDate} à ${birthPlace}, a assuré des vacations au sein de notre établissement en qualité de ${formateurTitle} en ${trainingName}.`;
  } else if (isTraining) {
    mainText = `A suivi avec succès la formation intitulée "${trainingName}", organisée par ${schoolName}. Cette formation, d'une durée de ${duration}, a permis à ${fullName} d'acquérir les compétences nécessaires dans le domaine de ${trainingName}.`;
  } else {
    mainText = `A effectué avec succès un stage intitulé "${trainingName}", au sein de ${schoolName}. Ce stage, d'une durée de ${duration}, a permis à ${fullName} d'acquérir une expérience pratique et les compétences professionnelles dans le domaine de ${trainingName}.`;
  }

  const endDateText = isVacation
    ? ""
    : isTraining
      ? `La date d'obtention de cette Attestation de Formation est le ${endDate}.`
      : `La date de fin de ce stage est le ${endDate}.`;

  const footerText = isVacation
    ? "Cette attestation est délivrée à l'intéressé(e) pour servir et valoir ce que de droit."
    : "Cette attestation est délivrée pour servir et valoir ce que de droit.";

  return {
    title,
    name: fullName,
    birthDateLine: `${genderNe} le : ${birthDate}`,
    mainText,
    endDateText,
    footerText,
    locationDateLine: `Fait à ${location}, le ${endDate}`,
  };
}

export default function Certificate({
  data: {
    student: { fullName, birthDate, sex, birthPlace },
    training,
    school,
    type,
    duration,
    endDate,
    colors,
    schoolLogo,
    location,
    overrides,
  },
}) {
  const ov = overrides || {};
  const DEFAULT_COLORS = {
    primary: "#3b82f6", // blue
    secondary: "#7c3aed", // violet
  };

  // Hex validator (accepts #RGB or #RRGGBB)
  const isValidHex = (c) =>
    typeof c === "string" && /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(c.trim());

  // treat pure black as invalid placeholder
  const isBlackHex = (c) => {
    if (!isValidHex(c)) return false;
    const v = c.trim().toLowerCase();
    return v === "#000" || v === "#000000";
  };

  // reject the specific red we saw before
  const isUnwantedRed = (c) => {
    if (!isValidHex(c)) return false;
    const v = c.trim().toLowerCase();
    return v === "#f5222d" || v === "#f5222dff"; // explicit forms
  };

  // Defensive merge: only accept valid non-black and non-unwanted colors
  const mergedColors = {
    primary:
      isValidHex(colors?.primary) && !isBlackHex(colors.primary) && !isUnwantedRed(colors.primary)
        ? colors.primary.trim()
        : DEFAULT_COLORS.primary,
    secondary:
      isValidHex(colors?.secondary) && !isBlackHex(colors.secondary) && !isUnwantedRed(colors.secondary)
        ? colors.secondary.trim()
        : DEFAULT_COLORS.secondary,
  };

  // Determine certificate type and content
  const isTraining = type === "training";
  const isVacation = type === "vacation";
  const certificateTitle = ov.title?.trim()
    ? ov.title.trim()
    : isVacation
      ? "ATTESTATION DE VACATIONS"
      : isTraining
        ? "ATTESTATION DE FORMATION"
        : "ATTESTATION DE STAGE";
  const trainingName = isTraining || isVacation ? training?.name : training?.titled;
  const schoolName = school?.name;
  const normalizedSex = typeof sex === "string" ? sex?.trim()?.toLowerCase() : "";
  const isFemale = ["female","Female"].includes(normalizedSex);

  const getMainContent = () => {
    if (ov.mainText?.trim()) {
      return ov.mainText.trim();
    }
    if (isVacation) {
       const formateurTitle = sex?.toLowerCase() === 'female' ? "formatrice" : "formateur";
       const genderStr = sex?.toLowerCase() === 'female' ? "née" : "né";
       return (
         <Text>
           <Text style={{ color: mergedColors.primary, fontWeight: "bold" }}>{fullName}</Text>, {genderStr} le {birthDate} à {birthPlace}, a assuré des vacations au sein de notre établissement en qualité de {formateurTitle} en {trainingName}.
         </Text>
       );
    } else if (isTraining) {
      return `A suivi avec succès la formation intitulée "${trainingName}", organisée par ${schoolName}. Cette formation, d'une durée de ${duration}, a permis à ${fullName} d'acquérir les compétences nécessaires dans le domaine de ${trainingName}.`;
    } else {
      return `A effectué avec succès un stage intitulé "${trainingName}", au sein de ${schoolName}. Ce stage, d'une durée de ${duration}, a permis à ${fullName} d'acquérir une expérience pratique et les compétences professionnelles dans le domaine de ${trainingName}.`;
    }
  };

  const getEndDateText = () => {
    if (ov.endDateText?.trim()) {
      return ov.endDateText.trim();
    }
    if (isVacation) {
      return ""; // Handled separately with location (Fait à...)
    } else if (isTraining) {
      return `La date d'obtention de cette Attestation de Formation est le ${endDate}.`;
    } else {
      return `La date de fin de ce stage est le ${endDate}.`;
    }
  };

  return (
    <Document style={{ overflow: "hidden" }} pageMode="fullScreen" pageLayout="singlePage">
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* corners */}
        <View style={{ ...styles.cornerTopRight, backgroundColor: mergedColors.secondary }} />
        <View style={{ ...styles.cornerBottomLeft, backgroundColor: mergedColors.primary }} />

        {/* Logos */}
        <Image src={schoolLogo || "/img/bignova-learning/logo.png"} style={styles.schoolLogo} />
        <Image src="/img/logo-purple-H.png" style={styles.platformLogo} />

        {/* Border */}
        <View
          style={{
            ...styles.border,
            borderBottomColor: mergedColors.secondary,
            borderLeftColor: mergedColors.secondary,
            borderTopColor: mergedColors.primary,
            borderRightColor: mergedColors.primary,
          }}
        />

        {/* Content */}
        <View style={styles.contentContainer}>
          <View style={styles.title}>
            <Text style={{ color: mergedColors.primary }}>{certificateTitle}</Text>
          </View>

          <View style={styles.section}>
            {isVacation ? (
              <Text>Nous soussignés, {schoolName || "Bignova Learning"}, attestons par la présente que :</Text>
            ) : (
              <Text>Nous, soussignés, certifions par la présente que :</Text>
            )}

            {!isVacation && (
               <>
                 <Text style={{ ...styles.name, color: mergedColors.primary }}>{ov.name?.trim() || fullName}</Text>
                 <Text style={{ marginTop: "8px" }}>
                   {ov.birthDateLine?.trim() || `${sex?.toLowerCase() === 'female' ? 'Née' : 'Né'} le : ${birthDate}`}
                 </Text>
               </>
            )}

            <Text style={{ marginTop: "12px", lineHeight: 1.4, textAlign: "justify", maxWidth: "600px" }}>
              {getMainContent()}
            </Text>

            {!isVacation && <Text style={{ marginTop: "12px" }}>{getEndDateText()}</Text>}

            <Text style={styles.footer}>
              {ov.footerText?.trim() ||
                (isVacation
                  ? "Cette attestation est délivrée à l'intéressé(e) pour servir et valoir ce que de droit."
                  : "Cette attestation est délivrée pour servir et valoir ce que de droit.")}
            </Text>

            {isVacation && (
              <View style={{ width: "100%", maxWidth: "600px", alignItems: "flex-end", marginTop: "16px" }}>
                <Text style={{ fontSize: 13 }}>{ov.locationDateLine?.trim() || `Fait à ${location}, le ${endDate}`}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signatureSection}>
          <View style={{ textAlign: "center" }}>
            <Text style={{ fontWeight: "bold", marginBottom: "40px" }}>Signature du Directeur</Text>
            <View style={{ width: "120px", height: "1px", backgroundColor: "#333", marginTop: "20px" }} />
          </View>
          <View style={{ textAlign: "center" }}>
            <Text style={{ fontWeight: "bold", marginBottom: "40px" }}>Cachet de l'École</Text>
            <View style={{ width: "120px", height: "1px", backgroundColor: "#333", marginTop: "20px" }} />
          </View>
        </View>

        {/* Footer */}
        <View style={styles.websiteSection}>
          <View style={{ ...styles.websiteDivider, backgroundColor: mergedColors.primary }} />
          <Text style={{ ...styles.websiteText, color: mergedColors.primary }}>BIGNOVALEARNING.COM</Text>
          <Text style={styles.poweredByText}>Plateforme de gestion éducative</Text>
        </View>
      </Page>
    </Document>
  );
}
